// 📝 고객 정보 입력 페이지 (수동 신청 후 주소/계좌 입력)
// [배경] 거래처 유입 고객이 알림톡 링크로 접근하여 주소/계좌 직접 입력
// [인증] 토큰 + 전화번호 맨 마지막 4자리 검증 (하이브리드 인증)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  PhoneAndroid as PhoneIcon,
  Home as HomeIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// ═══════════════════════════════════════════════════════════
// 상수 정의
// ═══════════════════════════════════════════════════════════

/** 은행 목록 */
const BANK_LIST = [
  '국민은행', '신한은행', '우리은행', '하나은행', 'NH농협',
  '기업은행', 'SC제일은행', '케이뱅크', '카카오뱅크', '토스뱅크',
  '새마을금고', '신협', '우체국', '수협', '부산은행', '대구은행',
  '경남은행', '광주은행', '전북은행', '제주은행', 'KDB산업은행',
];

/** API 베이스 URL */
const API_BASE_URL = 'https://us-central1-refurb-buy.cloudfunctions.net/api/api';

// ═══════════════════════════════════════════════════════════
// 타입 정의
// ═══════════════════════════════════════════════════════════

type PageStep = 'loading' | 'verify' | 'form' | 'success' | 'error';

interface ApplicationInfo {
  id: string;
  user_name: string;
  devices: { deviceType: string; deviceModel: string }[];
  needs_address: boolean;
  needs_bank: boolean;
}

// ═══════════════════════════════════════════════════════════
// 컴포넌트
// ═══════════════════════════════════════════════════════════

const CustomerInfoCompletePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // 📌 페이지 상태
  const [step, setStep] = useState<PageStep>('loading');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [userNameMasked, setUserNameMasked] = useState('');
  const [application, setApplication] = useState<ApplicationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 📌 전화번호 뒷4자리 검증 상태
  const [phoneLast4, setPhoneLast4] = useState(['', '', '', '']);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const MAX_ATTEMPTS = 5;

  // 📌 정보 입력 폼 상태
  const [showPostcode, setShowPostcode] = useState(false);
  const [formData, setFormData] = useState({
    pickupAddress: '',
    pickupAddressDetail: '',
    bankName: '',
    bankAccount: '',
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 📌 확인 다이얼로그 상태
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // 1단계: 토큰 유효성 확인
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('유효하지 않은 접근입니다. 알림톡의 링크를 다시 확인해주세요.');
        setStep('error');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/info-tokens/${token}/verify`);
        const result = await response.json();

        if (!result.valid) {
          switch (result.error_code) {
            case 'TOKEN_EXPIRED':
              setError('링크가 만료되었습니다. 관리자에게 새 링크를 요청해주세요.');
              break;
            case 'TOKEN_USED':
              setError('이미 정보 입력이 완료되었습니다.');
              break;
            case 'TOKEN_REVOKED':
              setError('이 링크는 더 이상 사용할 수 없습니다.');
              break;
            default:
              setError('유효하지 않은 링크입니다. 알림톡의 링크를 다시 확인해주세요.');
          }
          setStep('error');
          return;
        }

        setPhoneMasked(result.phone_masked || '');
        setUserNameMasked(result.user_name_masked || '');
        setStep('verify');
      } catch (err) {
        console.error('토큰 검증 오류:', err);
        setError('오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        setStep('error');
      }
    };

    verifyToken();
  }, [token]);

  // ═══════════════════════════════════════════════════════════
  // 2단계: 전화번호 뒷4자리 검증
  // ═══════════════════════════════════════════════════════════

  /** 뒷4자리 입력 핸들러 */
  const handlePhoneDigitChange = (index: number, value: string) => {
    // 숫자만 허용
    if (!/^\d?$/.test(value)) return;
    
    const newDigits = [...phoneLast4];
    newDigits[index] = value;
    setPhoneLast4(newDigits);
    setVerifyError(null);

    // 자동 포커스 이동
    if (value && index < 3) {
      const nextInput = document.getElementById(`phone-digit-${index + 1}`);
      nextInput?.focus();
    }
  };

  /** 백스페이스 처리 */
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !phoneLast4[index] && index > 0) {
      const prevInput = document.getElementById(`phone-digit-${index - 1}`);
      prevInput?.focus();
    }
  };

  /** 전화번호 검증 제출 */
  const handleVerifyPhone = async () => {
    const last4 = phoneLast4.join('');
    if (last4.length !== 4) {
      setVerifyError('4자리를 모두 입력해주세요.');
      return;
    }

    setVerifyLoading(true);
    setVerifyError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/info-tokens/${token}/authenticate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_last4: last4 }),
      });
      const result = await response.json();

      if (!result.authenticated) {
        const newAttempts = verifyAttempts + 1;
        setVerifyAttempts(newAttempts);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          setError('입력 횟수를 초과했습니다. 관리자에게 문의해주세요.');
          setStep('error');
          return;
        }
        
        setVerifyError(`전화번호가 일치하지 않습니다. (${MAX_ATTEMPTS - newAttempts}회 남음)`);
        // 입력 초기화
        setPhoneLast4(['', '', '', '']);
        document.getElementById('phone-digit-0')?.focus();
        return;
      }

      setApplication(result.application);
      setStep('form');
    } catch (err) {
      console.error('인증 오류:', err);
      setVerifyError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setVerifyLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 3단계: 정보 입력 및 제출
  // ═══════════════════════════════════════════════════════════

  /** 다음 주소 검색 완료 핸들러 */
  const handlePostcodeComplete = useCallback((data: any) => {
    setFormData(prev => ({
      ...prev,
      pickupAddress: data.roadAddress || data.jibunAddress || '',
    }));
    setShowPostcode(false);
  }, []);

  /** 주소 검색 열기 */
  const openPostcodeSearch = () => {
    // 다음 주소 검색 API 사용
    new (window as any).daum.Postcode({
      oncomplete: handlePostcodeComplete,
    }).open();
  };

  /** 계좌번호 포맷팅 (하이픈 자동 제거) */
  const formatAccountNumber = (value: string): string => {
    return value.replace(/[^0-9-]/g, '');
  };

  /** 유효성 검사 후 확인 다이얼로그 열기 */
  const handleOpenConfirm = () => {
    // 유효성 검사
    if (application?.needs_address) {
      if (!formData.pickupAddress.trim()) {
        setFormError('주소를 입력해주세요.');
        return;
      }
      if (!formData.pickupAddressDetail.trim()) {
        setFormError('상세 주소를 입력해주세요. (동/호수)');
        return;
      }
    }
    
    if (application?.needs_bank) {
      if (!formData.bankName) {
        setFormError('은행을 선택해주세요.');
        return;
      }
      const accountClean = formData.bankAccount.replace(/-/g, '');
      if (!accountClean || accountClean.length < 10) {
        setFormError('올바른 계좌번호를 입력해주세요. (최소 10자리)');
        return;
      }
    }

    setFormError(null);
    setShowConfirmDialog(true);
  };

  /** 확인 다이얼로그에서 최종 제출 */
  const handleConfirmSubmit = async () => {
    setSubmitLoading(true);
    setShowConfirmDialog(false);

    try {
      const response = await fetch(`${API_BASE_URL}/info-tokens/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_last4: phoneLast4.join(''),
          pickup_address: formData.pickupAddress.trim(),
          pickup_address_detail: formData.pickupAddressDetail.trim(),
          bank_name: formData.bankName,
          bank_account: formData.bankAccount.replace(/-/g, ''),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || '제출에 실패했습니다.');
      }

      setStep('success');
    } catch (err: any) {
      console.error('정보 제출 오류:', err);
      setFormError(err.message || '오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 렌더링: 로딩 화면
  // ═══════════════════════════════════════════════════════════

  if (step === 'loading') {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          bgcolor: '#f5f7fa' 
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">확인 중...</Typography>
        </Stack>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // 렌더링: 에러 화면
  // ═══════════════════════════════════════════════════════════

  if (step === 'error') {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          bgcolor: '#fef2f2', 
          p: 2 
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" color="error" gutterBottom fontWeight={600}>
              접근 오류
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/')}
              sx={{ borderRadius: 2 }}
            >
              홈으로 이동
            </Button>
          </Paper>
        </motion.div>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // 렌더링: 성공 화면
  // ═══════════════════════════════════════════════════════════

  if (step === 'success') {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          bgcolor: '#ecfdf5', 
          p: 2 
        }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, type: 'spring' }}
        >
          <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center', borderRadius: 3 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            </motion.div>
            <Typography variant="h5" gutterBottom fontWeight={700} color="success.dark">
              정보 입력 완료!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              입력하신 정보로 택배 수거가 진행됩니다.
              <br />
              진행 상황은 알림톡으로 안내드리겠습니다.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              이용해 주셔서 감사합니다.
            </Typography>
            <Button 
              variant="contained" 
              color="success"
              onClick={() => navigate('/')}
              sx={{ borderRadius: 2, px: 4 }}
            >
              홈으로 이동
            </Button>
          </Paper>
        </motion.div>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // 렌더링: 메인 (verify / form)
  // ═══════════════════════════════════════════════════════════

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        py: { xs: 3, md: 5 } 
      }}
    >
      <Container maxWidth="sm">
        {/* 상단 진행 단계 */}
        <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <Stepper activeStep={step === 'verify' ? 0 : 1} alternativeLabel>
            <Step>
              <StepLabel>본인 확인</StepLabel>
            </Step>
            <Step>
              <StepLabel>정보 입력</StepLabel>
            </Step>
          </Stepper>
        </Paper>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Paper sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 전화번호 뒷4자리 검증 화면 - 프리미엄 UI */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {step === 'verify' && (
              <Box>
                {/* 📌 상단 헤더 - 미니멀하고 고급스럽게 */}
                <Stack alignItems="center" sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.35)',
                    }}
                  >
                    <LockIcon sx={{ fontSize: 36, color: 'white' }} />
                  </Box>
                  <Typography 
                    variant="h5" 
                    fontWeight={800} 
                    textAlign="center"
                    sx={{ 
                      background: 'linear-gradient(135deg, #1a1a2e 0%, #4a4a6a 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    본인 확인
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mt: 0.5 }}
                  >
                    안녕하세요, <strong>{userNameMasked}</strong>님
                  </Typography>
                </Stack>

                {/* 📌 전화번호 표시 - 대형 프리미엄 카드 */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #f8f9ff 0%, #e8ecff 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.15)',
                    boxShadow: '0 4px 24px rgba(102, 126, 234, 0.1)',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    },
                  }}
                >
                  <Typography 
                    variant="overline" 
                    sx={{ 
                      color: '#667eea',
                      fontWeight: 600,
                      letterSpacing: '0.15em',
                      fontSize: '0.7rem',
                    }}
                  >
                    등록된 전화번호
                  </Typography>
                  
                  {/* 전화번호 대형 표시 */}
                  <Typography 
                    sx={{ 
                      fontSize: { xs: '2rem', sm: '2.5rem' },
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      fontFamily: '"SF Mono", "Roboto Mono", monospace',
                      color: '#1a1a2e',
                      my: 1.5,
                      textShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                  >
                    {phoneMasked}
                  </Typography>

                  {/* 강조 안내 */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                      px: 2.5,
                      py: 1,
                      borderRadius: 10,
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#667eea',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                        },
                      }}
                    />
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      sx={{ color: '#5a67d8' }}
                    >
                      **** 부분을 아래에 입력하세요
                    </Typography>
                  </Box>
                </Box>

                {/* 📌 4자리 입력 필드 - 프리미엄 스타일 */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    textAlign="center" 
                    sx={{ 
                      mb: 2, 
                      color: '#4a5568',
                    }}
                  >
                    전화번호 마지막 4자리 입력
                  </Typography>
                  <Stack 
                    direction="row" 
                    justifyContent="center" 
                    spacing={{ xs: 1.5, sm: 2 }}
                  >
                    {[0, 1, 2, 3].map((index) => (
                      <TextField
                        key={index}
                        id={`phone-digit-${index}`}
                        value={phoneLast4[index]}
                        onChange={(e) => handlePhoneDigitChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          style: { 
                            textAlign: 'center', 
                            fontSize: 32, 
                            fontWeight: 700,
                            padding: '16px 0',
                            fontFamily: '"SF Mono", "Roboto Mono", monospace',
                          },
                          inputMode: 'numeric',
                        }}
                        sx={{ 
                          width: { xs: 56, sm: 68 },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: phoneLast4[index] ? 'rgba(102, 126, 234, 0.08)' : 'white',
                            transition: 'all 0.2s ease',
                            border: '2px solid',
                            borderColor: phoneLast4[index] ? '#667eea' : 'transparent',
                            boxShadow: phoneLast4[index] 
                              ? '0 4px 12px rgba(102, 126, 234, 0.2)' 
                              : '0 2px 8px rgba(0,0,0,0.06)',
                            '&:hover': {
                              borderColor: '#667eea',
                            },
                            '&.Mui-focused': {
                              borderColor: '#667eea',
                              boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                            },
                            '& fieldset': {
                              border: 'none',
                            },
                          },
                        }}
                        autoFocus={index === 0}
                        disabled={verifyLoading}
                      />
                    ))}
                  </Stack>
                </Box>

                {verifyError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 2,
                      '& .MuiAlert-icon': { alignItems: 'center' },
                    }}
                  >
                    {verifyError}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleVerifyPhone}
                  disabled={verifyLoading || phoneLast4.some(d => !d)}
                  sx={{ 
                    borderRadius: 3, 
                    py: 1.8,
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: phoneLast4.every(d => d) 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
                    boxShadow: phoneLast4.every(d => d) 
                      ? '0 8px 24px rgba(102, 126, 234, 0.35)'
                      : 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.45)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    '&.Mui-disabled': {
                      background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)',
                      color: '#a0aec0',
                    },
                  }}
                >
                  {verifyLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    '본인 확인하기'
                  )}
                </Button>
              </Box>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* 정보 입력 화면 - 프리미엄 UI (컴팩트) */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {step === 'form' && application && (
              <Box>
                {/* 📌 상단 헤더 - 컴팩트 */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                      flexShrink: 0,
                    }}
                  >
                    <HomeIcon sx={{ fontSize: 24, color: 'white' }} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h6" 
                      fontWeight={700}
                      sx={{ 
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #4a4a6a 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: 1.2,
                      }}
                    >
                      정보 입력
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{application.user_name}</strong>님
                    </Typography>
                  </Box>
                </Stack>

                {/* 📌 신청 기기 정보 - 컴팩트 */}
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: '#059669' }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#059669',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                      }}
                    >
                      신청 기기
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5}>
                    {application.devices.map((device, index) => (
                      <Stack 
                        key={index} 
                        direction="row" 
                        spacing={1} 
                        alignItems="center"
                        sx={{
                          py: 0.75,
                          px: 1,
                          bgcolor: 'white',
                          borderRadius: 1.5,
                        }}
                      >
                        <Chip 
                          label={device.deviceType} 
                          size="small"
                          sx={{ 
                            bgcolor: '#10b981',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 10,
                            height: 22,
                          }}
                        />
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: 13 }}>
                          {device.deviceModel}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {/* 📌 주소 입력 - 컴팩트 */}
                {application.needs_address && (
                  <Box 
                    sx={{ 
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#fafbfc',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <HomeIcon sx={{ fontSize: 16, color: 'white' }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a1a2e' }}>
                        수거 주소
                      </Typography>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.pickupAddress}
                      placeholder="주소 검색을 클릭하세요"
                      onClick={openPostcodeSearch}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton 
                              onClick={openPostcodeSearch} 
                              edge="end"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                              }}
                            >
                              <SearchIcon sx={{ color: '#667eea', fontSize: 20 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: 'white',
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.pickupAddressDetail}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        pickupAddressDetail: e.target.value 
                      }))}
                      placeholder="상세 주소 (동/호수)"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: 'white',
                        },
                      }}
                    />
                  </Box>
                )}

                {/* 📌 계좌 정보 입력 - 컴팩트 */}
                {application.needs_bank && (
                  <Box 
                    sx={{ 
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#fafbfc',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BankIcon sx={{ fontSize: 16, color: 'white' }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1a1a2e' }}>
                        입금 계좌
                      </Typography>
                    </Stack>
                    <FormControl 
                      fullWidth 
                      size="small"
                      sx={{ 
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: 'white',
                        },
                      }}
                    >
                      <InputLabel>은행 선택</InputLabel>
                      <Select
                        value={formData.bankName}
                        label="은행 선택"
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          bankName: e.target.value 
                        }))}
                      >
                        {BANK_LIST.map((bank) => (
                          <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.bankAccount}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        bankAccount: formatAccountNumber(e.target.value) 
                      }))}
                      placeholder="계좌번호 (숫자만 입력)"
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: 'white',
                        },
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#92400e', 
                        fontWeight: 500, 
                        mt: 1, 
                        display: 'block',
                        fontSize: 11,
                      }}
                    >
                      * 본인 명의 계좌만 입력 가능
                    </Typography>
                  </Box>
                )}

                {formError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: 1.5,
                      py: 0.5,
                      '& .MuiAlert-icon': { alignItems: 'center' },
                    }}
                  >
                    {formError}
                  </Alert>
                )}

                {/* 📌 제출 버튼 - 컴팩트 */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleOpenConfirm}
                  disabled={submitLoading}
                  sx={{ 
                    borderRadius: 2, 
                    py: 1.5,
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
                    },
                    '&.Mui-disabled': {
                      background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)',
                      color: '#a0aec0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    '정보 제출하기'
                  )}
                </Button>
              </Box>
            )}
          </Paper>
        </motion.div>

        {/* 하단 안내 */}
        <Typography 
          variant="caption" 
          color="rgba(255,255,255,0.7)" 
          textAlign="center" 
          sx={{ display: 'block', mt: 3 }}
        >
          © 다원트레이드
        </Typography>
      </Container>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* 📌 정보 확인 다이얼로그 - 프리미엄 UI */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* 헤더 */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <WarningIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="white">
              입력 정보 확인
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
              아래 정보가 정확한지 확인해주세요
            </Typography>
          </Box>

          {/* 정보 표시 영역 */}
          <Box sx={{ p: 3 }}>
            {/* 주소 정보 */}
            {application?.needs_address && (
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HomeIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#1a1a2e">
                    수거 주소
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography 
                    variant="body1" 
                    fontWeight={600} 
                    sx={{ color: '#1a1a2e', wordBreak: 'keep-all' }}
                  >
                    {formData.pickupAddress}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ color: '#64748b', mt: 0.5 }}
                  >
                    {formData.pickupAddressDetail}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* 계좌 정보 */}
            {application?.needs_bank && (
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#fffbeb',
                  border: '1px solid #fde68a',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BankIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#1a1a2e">
                    입금 계좌
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    border: '1px solid #fde68a',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Chip
                      label={formData.bankName}
                      size="small"
                      sx={{
                        bgcolor: '#f59e0b',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    <Typography 
                      variant="body1" 
                      fontWeight={600}
                      sx={{ 
                        color: '#1a1a2e',
                        fontFamily: '"SF Mono", "Roboto Mono", monospace',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {formData.bankAccount}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}

            {/* 주의 문구 */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <ErrorIcon sx={{ color: '#dc2626', fontSize: 20, mt: 0.25 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#991b1b' }}>
                  정보가 정확한가요?
                </Typography>
                <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                  잘못된 정보 입력 시 택배 수거 및 입금이 지연될 수 있습니다.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* 버튼 영역 */}
          <Box sx={{ p: 3, pt: 0 }}>
            <Stack direction="row" spacing={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowConfirmDialog(false)}
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1.3,
                  fontWeight: 600,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#cbd5e0',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                수정하기
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleConfirmSubmit}
                startIcon={<CheckIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1.3,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
                  },
                }}
              >
                확인 완료
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CustomerInfoCompletePage;

